import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface LoanSupervisorSelectProps {
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * Loan Supervisor select component.
 * Fetches loan supervisors from the 'loanSupervisor' provider.
 *
 * Used for parameter type: LOAN_SUPERVISOR_SELECT
 */
const LoanSupervisorSelect: React.FC<LoanSupervisorSelectProps> = ({
  disabled,
  placeholder,
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const params = useMemo(() => ({
    search,
    fieldName: 'loanSupervisor',
    entityType: 'REPORT',
  }), [search]);

  const { data: options, isLoading, error } = useGetSelectFieldValues('loanSupervisor', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'Select loan supervisor'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load loan supervisors' : isLoading ? 'Loading...' : 'No loan supervisors'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default LoanSupervisorSelect;
